// Module `workforce`: People and organisation
// (docs/superpowers/plans/2026-10-11-samenwerkingsverbanden.md). The personnel
// register and the org tree, general for every organisation. Request bodies
// are zod schemas; responses are plain types. All routes are org-level
// (/api/..., the session's org).
//
// Who sees what: owners and admins everything; members the tree and the
// register without scale, hours, email or workload (those fields are null);
// viewers the tree and the register only.
import { z } from "zod";
import { uuid } from "../schemas/common";
import { email, isoDate, text, textOrEmpty, type Id } from "./common";

// ---------- building blocks ----------

/** Workload colour: ≤ amberPct green, ≤ redPct amber, above red. */
export const WORKLOAD_COLOURS = ["green", "amber", "red"] as const;
export type WorkloadColour = (typeof WORKLOAD_COLOURS)[number];

/** A number with at most one decimal (hours). */
const oneDecimal = (min: number, max: number, label: string) =>
  z
    .number({ invalid_type_error: `Enter ${label}.` })
    .min(min, `${label[0].toUpperCase()}${label.slice(1)} must be at least ${min}.`)
    .max(max, `${label[0].toUpperCase()}${label.slice(1)} must be ${max} or less.`)
    .refine((n) => Math.abs(Math.round(n * 10) - n * 10) < 1e-9, "Use at most one decimal.");

/** A date or null ("" becomes null). */
const isoDateOrNull = z
  .union([isoDate, z.literal("")])
  .nullable()
  .transform((v) => (v === "" ? null : v));

const nonEmpty = <T extends z.ZodRawShape>(o: z.ZodObject<T>) => o.partial().refine((v) => Object.keys(v).length > 0, "Nothing to change.");

/** A unit or round in a breadcrumb or picker. */
export type UnitRef = { id: Id; name: string; kindName: string };

// ---------- settings: GET/PUT /api/workforce/settings ----------

export type WorkforceSettings = {
  /** A full-time week in hours (36). */
  fulltimeHours: number;
  /** fulltimeHours / 5 (derived, read only). */
  hoursPerDay: number;
  weeksPerQuarter: number;
  amberPct: number;
  redPct: number;
  /** The customer's word, e.g. "Samenwerkingsverband" (default "Collaboration"). */
  collabLabel: string;
  /** e.g. "Samenwerkingsverbanden" (default "Collaborations"); the sub-navigation uses it. */
  collabLabelPlural: string;
  /** Owners and admins may act as an employee (?as=) to fill in or confirm. */
  actAsEnabled: boolean;
  /** How numbers, money and dates are written, from the organisation's country (read only): NL is "nl-NL", else "en-GB". */
  locale: OrgLocale;
};

export type OrgLocale = "nl-NL" | "en-GB";

/** The organisation's number locale from its country (ISO 3166 alpha-2, as stored; "Netherlands" is accepted too). */
export function orgLocaleOf(country: string | null | undefined): OrgLocale {
  const c = (country ?? "").trim().toUpperCase();
  return c === "NL" || c === "NLD" || c === "NETHERLANDS" || c === "THE NETHERLANDS" ? "nl-NL" : "en-GB";
}

export const workforceSettingsBody = z
  .object({
    fulltimeHours: oneDecimal(1, 80, "the hours of a full-time week"),
    weeksPerQuarter: z.number().int().min(1, "At least 1 week.").max(14, "At most 14 weeks."),
    amberPct: z.number().int().min(1).max(300),
    redPct: z.number().int().min(1).max(300),
    collabLabel: text(60, "The word"),
    collabLabelPlural: text(60, "The plural"),
    actAsEnabled: z.boolean(),
  })
  .refine((v) => v.redPct >= v.amberPct, { message: "The red threshold must be at least the amber one.", path: ["redPct"] });
export type WorkforceSettingsBody = z.infer<typeof workforceSettingsBody>;

// ---------- unit kinds: GET/PUT /api/org-unit-kinds ----------

export type OrgUnitKind = {
  id: Id;
  name: string;
  sortOrder: number;
  /** A unit of this kind is a team: it gets its own caseload collab. */
  isTeam: boolean;
  /** Units of this kind (a kind in use cannot be removed: 409). */
  unitCount: number;
};

/** PUT replaces the list in this order (sortOrder = position); id null = new, existing ids are kept. */
export const orgUnitKindsBody = z.object({
  kinds: z
    .array(z.object({ id: uuid.nullable(), name: text(60, "Name"), isTeam: z.boolean() }))
    .min(1, "Add at least one kind.")
    .max(30)
    .refine((ks) => new Set(ks.map((k) => k.name.toLowerCase())).size === ks.length, "Each kind needs its own name."),
});
export type OrgUnitKindsBody = z.infer<typeof orgUnitKindsBody>;

// ---------- units: /api/org-units ----------

/** GET /api/org-units: flat, active units only; the screen builds the tree from parentId. */
export type OrgUnit = {
  id: Id;
  parentId: Id | null;
  kindId: Id;
  kindName: string;
  isTeam: boolean;
  name: string;
  shortName: string;
  managerEmployeeId: Id | null;
  /** "Jan van der Berg", or null without a manager. */
  managerName: string | null;
  sortOrder: number;
  /** Active employees with this unit as home unit. */
  headcountDirect: number;
  /** headcountDirect plus all units below. */
  headcountTotal: number;
  /** The team's own caseload collab, when the unit is a team and it exists. */
  caseloadCollabId: Id | null;
};

export const orgUnitBody = z.object({
  /** null: a top-level unit. Same org; no cycles (409). */
  parentId: uuid.nullable(),
  kindId: uuid,
  name: text(160, "Name"),
  shortName: textOrEmpty(40, "Short name").default(""),
  managerEmployeeId: uuid.nullable().default(null),
  sortOrder: z.number().int().min(0).max(100_000).default(0),
});
export type OrgUnitBody = z.infer<typeof orgUnitBody>;
export const orgUnitPatch = nonEmpty(
  z.object({
    parentId: uuid.nullable(),
    kindId: uuid,
    name: text(160, "Name"),
    shortName: textOrEmpty(40, "Short name"),
    managerEmployeeId: uuid.nullable(),
    sortOrder: z.number().int().min(0).max(100_000),
  }),
);
export type OrgUnitPatch = z.infer<typeof orgUnitPatch>;
export const orgUnitParams = z.object({ unitId: uuid });

/** One collab the unit's people work on, in the latest round (GET /api/org-units/:unitId/overview). */
export type UnitCollabRow = {
  collabId: Id;
  name: string;
  kindName: string;
  isCaseload: boolean;
  /** Sponsored by this unit or a unit below it ("own"), or elsewhere. */
  own: boolean;
  sponsorUnitName: string;
  /** People of this unit (and below) with a line on it. */
  people: number;
  /** Their days per week on it, summed. Null unless the caller is an owner/admin or manages this unit or a unit above it. */
  daysPerWeek: number | null;
};

/** GET /api/org-units/:unitId/overview */
export type UnitOverview = {
  unit: OrgUnit;
  /** From the top down to the unit's parent (breadcrumb). */
  ancestors: UnitRef[];
  /** Direct sub-units. */
  children: OrgUnit[];
  /** Direct people (home unit = this unit). */
  people: EmployeeListItem[];
  /** The round the collab figures come from (the open one, else the latest), or null. */
  round: { id: Id; label: string } | null;
  collabs: UnitCollabRow[];
  /** The collab rows carry days (owner/admin, or a manager of this unit or a unit above it). */
  canSeeDays: boolean;
};

// ---------- employees: /api/employees ----------

/** One person in a list (register, unit page, act-as picker). Restricted fields are null for members and viewers. */
export type EmployeeListItem = {
  id: Id;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  /** "Jan van der Berg" */
  name: string;
  /** "van der Berg, Jan" (sorting, register). */
  sortName: string;
  /** "Jan van der Berg": how lists show a person (shared displayName). */
  displayName: string;
  jobTitle: string;
  homeUnitId: Id;
  homeUnitName: string;
  /** The top-level unit above the home unit (directie), or null when the home unit is top-level. */
  departmentId: Id | null;
  departmentName: string | null;
  /** Restricted (owner/admin): null otherwise. */
  scale: string | null;
  /** Restricted (owner/admin): null otherwise. */
  contractHours: number | null;
  /** Workload % in the latest round with a submitted statement; null when restricted or none. */
  workloadPct: number | null;
  workloadColour: WorkloadColour | null;
  /** end_date not passed. */
  active: boolean;
  /** Has a login (user_id set). Restricted: null when the caller may not see it. */
  hasLogin: boolean | null;
};

/** The register's sortable columns. "name" sorts by surname without its prefix (shared surnameSortKey): "de Boer" under B. */
export const EMPLOYEE_SORTS = ["name", "jobTitle", "unit", "scale", "hours"] as const;
export type EmployeeSort = (typeof EMPLOYEE_SORTS)[number];

/** GET /api/employees?q=&unitId=&deep=1&page=&sort=&dir= (50 a page, by surname unless sorted otherwise). */
export const employeesQuery = z.object({
  q: z.string().trim().max(100).optional(),
  unitId: uuid.optional(),
  /** "1": include everyone below unitId. */
  deep: z.enum(["0", "1"]).optional(),
  /** 1-based. */
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  /** "1": include people whose end date has passed. */
  ended: z.enum(["0", "1"]).optional(),
  /** Default "name". "scale" and "hours" sort only for owners and admins (others get "name": the order would show restricted fields). */
  sort: z.enum(EMPLOYEE_SORTS).optional(),
  /** Default "asc". Ties sort by surname, ascending. */
  dir: z.enum(["asc", "desc"]).optional(),
});
export type EmployeesQuery = z.infer<typeof employeesQuery>;
export const EMPLOYEE_PAGE_SIZE = 50;

export type EmployeePage = { items: EmployeeListItem[]; total: number; page: number; pageSize: number };

/** A line of a past or current statement, on the employee page. */
export type EmployeeStatementLine = { collabId: Id; collabName: string; daysPerWeek: number; status: "pending" | "confirmed" | "questioned" };

/** One statement per round on the employee page. */
export type EmployeeStatement = {
  attestationId: Id;
  roundId: Id;
  /** "Q1 2027" */
  roundLabel: string;
  roundStatus: "open" | "closed";
  status: "draft" | "submitted";
  /** A draft in a closed round: "not received". */
  notReceived: boolean;
  totalDaysPerWeek: number;
  workloadPct: number;
  workloadColour: WorkloadColour;
  lines: EmployeeStatementLine[];
};

/** GET /api/employees/:employeeId */
export type Employee = EmployeeListItem & {
  /** Restricted (owner/admin): null otherwise. */
  email: string | null;
  /** Restricted (owner/admin): null otherwise. */
  userId: Id | null;
  startDate: string;
  endDate: string | null;
  /** The place in the tree, from the top down to the home unit. */
  unitPath: UnitRef[];
  managedUnits: { id: Id; name: string }[];
  ownedCollabs: { id: Id; name: string }[];
  /**
   * Newest first. null when the caller may not see this person's workload
   * (members other than the person or their line manager; viewers).
   */
  statements: EmployeeStatement[] | null;
};

export const employeeBody = z
  .object({
    employeeNumber: textOrEmpty(20, "Employee number").default(""),
    firstName: text(80, "First name"),
    /** Prefixes at the start: "van der Berg". */
    lastName: text(120, "Last name"),
    email: z
      .union([email, z.literal("")])
      .nullable()
      .default(null)
      .transform((v) => (v === "" ? null : v)),
    jobTitle: text(160, "Job title"),
    scale: textOrEmpty(10, "Scale").default(""),
    contractHours: oneDecimal(1, 80, "the contract hours"),
    homeUnitId: uuid,
    /** Link to an account of the organisation (their own statement); null = none. */
    userId: uuid.nullable().default(null),
    startDate: isoDate,
    endDate: isoDateOrNull.default(null),
  })
  .refine((v) => !v.endDate || v.endDate >= v.startDate, { message: "The end date cannot be before the start date.", path: ["endDate"] });
export type EmployeeBody = z.infer<typeof employeeBody>;
export const employeePatch = z
  .object({
    employeeNumber: textOrEmpty(20, "Employee number"),
    firstName: text(80, "First name"),
    lastName: text(120, "Last name"),
    email: z
      .union([email, z.literal("")])
      .nullable()
      .transform((v) => (v === "" ? null : v)),
    jobTitle: text(160, "Job title"),
    scale: textOrEmpty(10, "Scale"),
    contractHours: oneDecimal(1, 80, "the contract hours"),
    homeUnitId: uuid,
    userId: uuid.nullable(),
    startDate: isoDate,
    endDate: isoDateOrNull,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "Nothing to change.")
  .refine((v) => !v.endDate || !v.startDate || v.endDate >= v.startDate, { message: "The end date cannot be before the start date.", path: ["endDate"] });
export type EmployeePatch = z.infer<typeof employeePatch>;
export const employeeParams = z.object({ employeeId: uuid });

// ---------- salary scales: GET/PUT /api/salary-scales?year= ----------

export type SalaryScale = {
  id: Id;
  year: number;
  /** "11" */
  scale: string;
  hourlyRateCents: number;
  /** Active employees on this scale (a hint on the settings screen). */
  employees: number;
};

export const salaryScalesQuery = z.object({ year: z.coerce.number().int().min(2000).max(2100) });
/** PUT replaces that year's scales. */
export const salaryScalesBody = z.object({
  scales: z
    .array(
      z.object({
        scale: text(10, "Scale"),
        hourlyRateCents: z.number().int("Whole cents.").min(0, "Cannot be negative.").max(1_000_000, "The rate is too large."),
      }),
    )
    .max(100)
    .refine((ss) => new Set(ss.map((s) => s.scale.toLowerCase())).size === ss.length, "Each scale only once."),
});
export type SalaryScalesBody = z.infer<typeof salaryScalesBody>;

// ---------- act as (owner/admin, when actAsEnabled) ----------

/** ?as=<employeeId> on the statement and confirmation routes. */
export const actAsQuery = z.object({ as: uuid.optional() });
/** GET /api/act-as/employees?q= returns EmployeeListItem[] (at most 20). */
export const actAsEmployeesQuery = z.object({ q: z.string().trim().max(100).optional() });

// ---------- request input types (what the web sends: defaults may be left out) ----------

export type WorkforceSettingsInput = z.input<typeof workforceSettingsBody>;
export type OrgUnitKindsInput = z.input<typeof orgUnitKindsBody>;
export type OrgUnitInput = z.input<typeof orgUnitBody>;
export type OrgUnitPatchInput = z.input<typeof orgUnitPatch>;
export type EmployeeInput = z.input<typeof employeeBody>;
export type EmployeePatchInput = z.input<typeof employeePatch>;
export type EmployeesQueryInput = z.input<typeof employeesQuery>;
export type SalaryScalesInput = z.input<typeof salaryScalesBody>;
